import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { Menu, X, User, Search, Home, Newspaper, TrendingUp, BookOpen, ChevronRight, Twitter, Linkedin, Instagram, Facebook, Youtube, Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

// Brand Colors - Forbes Style
const FORBES_RED = '#E31B23';
const FORBES_BLACK = '#000000';

// ============================================
// FORBES-STYLE LOGO - White on black header
// ============================================
const Logo = ({ variant = 'dark', size = 'default' }) => {
  const color = variant === 'light' ? '#FFFFFF' : FORBES_BLACK;
  const redColor = FORBES_RED;
  
  // Mobile: Single line compact version
  if (size === 'small') {
    return (
      <div className="flex items-center gap-2">
        <span 
          className="font-bold text-base tracking-[0.15em]"
          style={{ fontFamily: "Georgia, 'Times New Roman', serif", color }}
        >
          INFLUENCIA
        </span>
        <span 
          className="font-normal text-sm"
          style={{ fontFamily: "Georgia, 'Times New Roman', serif", color: redColor }}
        >
          &
        </span>
        <span 
          className="font-bold text-base tracking-[0.15em]"
          style={{ fontFamily: "Georgia, 'Times New Roman', serif", color }}
        >
          PODER
        </span>
      </div>
    );
  }
  
  // Desktop: Stacked version
  return (
    <div className="flex flex-col items-center leading-none">
      <span 
        className="font-bold tracking-wide text-2xl lg:text-4xl"
        style={{ fontFamily: "Georgia, 'Times New Roman', serif", color }}
      >
        INFLUENCIA
      </span>
      <div className="flex items-center gap-1 lg:gap-2 my-0.5">
        <div className="h-px w-6 lg:w-10" style={{ backgroundColor: redColor }} />
        <span 
          className="font-normal text-sm lg:text-lg"
          style={{ fontFamily: "Georgia, 'Times New Roman', serif", color: redColor }}
        >
          &
        </span>
        <div className="h-px w-6 lg:w-10" style={{ backgroundColor: redColor }} />
      </div>
      <span 
        className="font-bold tracking-wide text-2xl lg:text-4xl"
        style={{ fontFamily: "Georgia, 'Times New Roman', serif", color }}
      >
        PODER
      </span>
    </div>
  );
};

// ============================================
// MOBILE BOTTOM NAV - Fitts Law (large touch targets in thumb zone)
// ============================================
const MobileBottomNav = () => {
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  
  // Miller's Law: Max 5 items for easy recall
  const navItems = [
    { icon: Home, label: 'Inicio', href: '/' },
    { icon: Newspaper, label: 'Noticias', href: '/dossiers' },
    { icon: TrendingUp, label: 'Índice', href: '/influence-index' },
    { icon: BookOpen, label: 'Edición', href: '/edition' },
    { icon: User, label: user ? 'Perfil' : 'Entrar', href: user ? '/profile' : '/login' },
  ];

  return (
    <nav 
      className="lg:hidden fixed bottom-0 left-0 right-0 bg-black border-t border-gray-800 z-50 safe-area-bottom"
      data-testid="mobile-bottom-nav"
    >
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href || 
            (item.href !== '/' && location.pathname.startsWith(item.href));
          
          return (
            <Link
              key={item.href}
              to={item.href}
              className={`flex flex-col items-center justify-center min-w-[64px] min-h-[48px] px-3 py-2 rounded-lg transition-colors ${
                isActive 
                  ? 'text-white bg-gray-800' 
                  : 'text-gray-400 active:bg-gray-800'
              }`}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
            >
              <Icon className={`w-6 h-6 ${isActive ? 'text-red-500' : ''}`} />
              <span className="text-[10px] mt-1 font-medium">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

// ============================================
// FORBES-STYLE HEADER (Optimized for Mobile UX Laws)
// ============================================
export const Header = () => {
  const { user, logout, isAdmin } = useAuth();
  const { navigation, categories, getActiveNavigation, getActiveCategories } = useSiteConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Get active navigation items
  const activeNav = getActiveNavigation();
  const activeCategories = getActiveCategories();
  
  // Build navigation items from config
  const navItems = activeNav.length > 0 
    ? activeNav.map(item => ({ label: item.label, href: item.url }))
    : [
        { label: 'Noticias', href: '/dossiers' },
        { label: 'Índice', href: '/influence-index' },
        { label: 'Edición', href: '/edition' },
      ];
  
  // Add categories to navigation
  const categoryNavItems = activeCategories.map(cat => ({
    label: cat.name,
    href: `/dossiers?sector=${cat.slug}`,
  }));

  const secondaryNav = [
    { label: 'Listas', href: '/lists' },
    { label: 'Voces', href: '/evidence' },
  ];

  // Mobile menu categories - Hick's Law: Group related items
  const mobileCategories = [
    {
      title: 'Contenido',
      items: navItems.filter(item => !item.href.includes('sector='))
    },
    {
      title: 'Secciones',
      items: categoryNavItems.length > 0 ? categoryNavItems : [
        { label: 'Economía', href: '/dossiers?sector=economia' },
        { label: 'Política', href: '/dossiers?sector=politica' },
        { label: 'Negocios', href: '/dossiers?sector=negocios' },
      ]
    },
    {
      title: 'Más',
      items: secondaryNav
    }
  ];

  return (
    <>
      <header className="sticky top-0 z-50" data-testid="main-header">
        {/* BLACK TOP BAR - Forbes Style */}
        <div className="bg-black">
          <div className="max-w-7xl mx-auto px-4">
            {/* Mobile Header - Fitts Law: Large touch targets (min 48px) */}
            <div className="lg:hidden flex items-center justify-between h-14">
              {/* Menu Button - 48x48 touch target */}
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="flex items-center justify-center w-12 h-12 text-white rounded-lg active:bg-gray-800 transition-colors"
                aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              
              {/* Logo - Centered */}
              <Link to="/" className="flex-shrink-0" onClick={() => setMobileMenuOpen(false)}>
                <Logo variant="light" size="small" />
              </Link>
              
              {/* Search Button - 48x48 touch target */}
              <button 
                className="flex items-center justify-center w-12 h-12 text-white rounded-lg active:bg-gray-800 transition-colors"
                aria-label="Buscar"
                data-testid="mobile-search-btn"
              >
                <Search className="w-5 h-5" />
              </button>
            </div>

            {/* Desktop Header */}
            <div className="hidden lg:flex flex-col items-center py-6">
              <Link to="/" className="mb-4">
                <Logo variant="light" />
              </Link>
            </div>
          </div>
        </div>

        {/* NAVIGATION BAR - Desktop only */}
        <nav className="hidden lg:block bg-black border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center h-12">
              {/* Main Nav */}
              <div className="flex items-center gap-8">
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`text-sm font-medium transition-colors ${
                      location.pathname === item.href 
                        ? 'text-white' 
                        : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
                
                {/* Divider */}
                <span className="text-gray-600">|</span>
                
                {/* Secondary Nav */}
                {secondaryNav.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="text-sm text-gray-300 hover:text-white transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              {/* User Menu - Desktop */}
              <div className="absolute right-4 flex items-center gap-4">
                {user ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors">
                      <User className="w-5 h-5" />
                      <span className="text-sm">{user.name?.split(' ')[0]}</span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => navigate('/profile')}>
                        Mi Perfil
                      </DropdownMenuItem>
                      {isAdmin && (
                        <>
                          <DropdownMenuItem onClick={() => navigate('/admin')}>
                            Panel Editorial
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate('/admin/staff')}>
                            Gestión de Equipo
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={logout} className="text-red-600">
                        Cerrar Sesión
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <div className="flex items-center gap-3">
                    <Link 
                      to="/login"
                      className="text-sm text-gray-300 hover:text-white transition-colors"
                    >
                      Iniciar Sesión
                    </Link>
                    <Link 
                      to="/subscription"
                      className="text-sm font-bold text-white bg-red-600 hover:bg-red-700 px-4 py-2 rounded transition-colors"
                    >
                      Suscríbete
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* MOBILE MENU - Optimized with UX Laws */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-14 bg-black z-40 overflow-y-auto pb-20">
            <div className="px-4 py-4">
              {/* User Section First - Important for engagement */}
              {user ? (
                <div className="bg-gray-900 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{user.name}</p>
                      <p className="text-gray-400 text-sm">{user.email}</p>
                    </div>
                  </div>
                  {/* Quick Actions - Fitts Law: Large buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <Link
                      to="/profile"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-center gap-2 bg-gray-800 text-white py-3 px-4 rounded-lg active:bg-gray-700 transition-colors min-h-[48px]"
                    >
                      Mi Perfil
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center justify-center gap-2 bg-red-600 text-white py-3 px-4 rounded-lg active:bg-red-700 transition-colors min-h-[48px]"
                      >
                        Admin
                      </Link>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-900 rounded-xl p-4 mb-6">
                  <p className="text-gray-400 text-sm mb-3">Accede a contenido exclusivo</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-center bg-white text-black py-3 px-4 rounded-lg font-medium active:bg-gray-200 transition-colors min-h-[48px]"
                    >
                      Iniciar Sesión
                    </Link>
                    <Link
                      to="/subscription"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-center bg-red-600 text-white py-3 px-4 rounded-lg font-medium active:bg-red-700 transition-colors min-h-[48px]"
                    >
                      Suscríbete
                    </Link>
                  </div>
                </div>
              )}

              {/* Categories - Miller's Law: Grouped in chunks */}
              {mobileCategories.map((category, idx) => (
                <div key={category.title} className={`${idx > 0 ? 'mt-6' : ''}`}>
                  <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2 px-2">
                    {category.title}
                  </h3>
                  <div className="bg-gray-900 rounded-xl overflow-hidden">
                    {category.items.map((item, itemIdx) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center justify-between px-4 py-4 text-white active:bg-gray-800 transition-colors min-h-[56px] ${
                          itemIdx > 0 ? 'border-t border-gray-800' : ''
                        }`}
                      >
                        <span className="text-base">{item.label}</span>
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      </Link>
                    ))}
                  </div>
                </div>
              ))}

              {/* Logout - Bottom, separate */}
              {user && (
                <div className="mt-8">
                  <button
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    className="w-full flex items-center justify-center bg-gray-900 text-red-500 py-4 rounded-xl font-medium active:bg-gray-800 transition-colors min-h-[56px]"
                  >
                    Cerrar Sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </>
  );
};

// ============================================
// FORBES-STYLE FOOTER (Dynamic Configuration)
// ============================================
export const Footer = () => {
  const { footer, seo } = useSiteConfig();
  const currentYear = new Date().getFullYear();
  
  // Social icons mapping
  const socialIcons = {
    twitter: Twitter,
    linkedin: Linkedin,
    instagram: Instagram,
    facebook: Facebook,
    youtube: Youtube,
    website: Globe,
  };
  
  // Get footer links from config or use defaults
  const footerLinks = footer.footer_links?.length > 0 
    ? footer.footer_links 
    : [
        { label: 'Sobre Nosotros', href: '/about', section: 'company' },
        { label: 'Contacto', href: '/contact', section: 'company' },
        { label: 'Privacidad', href: '/privacy', section: 'legal' },
        { label: 'Términos', href: '/terms', section: 'legal' },
        { label: 'Publicidad', href: '/advertise', section: 'company' },
      ];
  
  const socialLinks = footer.social_links || [];
  const companyName = footer.company_name || 'Influencia & Poder';
  const tagline = footer.tagline || 'El medio de referencia en política y negocios';
  const copyrightText = footer.copyright_text || `© ${currentYear} ${companyName}. Todos los derechos reservados.`;
  
  return (
    <footer className="bg-black text-white pb-20 lg:pb-0">
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Logo */}
        <div className="flex justify-center mb-4">
          <Logo variant="light" />
        </div>
        
        {/* Tagline */}
        {tagline && (
          <p className="text-center text-gray-400 text-sm mb-8">{tagline}</p>
        )}
        
        {/* Social Links */}
        {socialLinks.length > 0 && (
          <div className="flex justify-center gap-4 mb-8">
            {socialLinks.map((link, idx) => {
              const Icon = socialIcons[link.platform] || Globe;
              return (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
                  aria-label={link.platform}
                >
                  <Icon className="w-5 h-5 text-white" />
                </a>
              );
            })}
          </div>
        )}
        
        {/* Links - Fitts Law: Adequate spacing for touch */}
        <div className="flex flex-wrap justify-center gap-4 lg:gap-6 text-sm text-gray-400 mb-8">
          {footerLinks.map((link, idx) => (
            <Link 
              key={idx}
              to={link.url || link.href} 
              className="py-2 px-1 hover:text-white transition-colors min-h-[44px] flex items-center"
            >
              {link.label}
            </Link>
          ))}
        </div>
        
        {/* Contact Info */}
        {(footer.contact_email || footer.contact_phone) && (
          <div className="text-center text-sm text-gray-500 mb-4">
            {footer.contact_email && (
              <a href={`mailto:${footer.contact_email}`} className="hover:text-white mr-4">
                {footer.contact_email}
              </a>
            )}
            {footer.contact_phone && (
              <a href={`tel:${footer.contact_phone}`} className="hover:text-white">
                {footer.contact_phone}
              </a>
            )}
          </div>
        )}
        
        {/* Copyright */}
        <div className="text-center text-sm text-gray-500">
          <p>{copyrightText.includes('{year}') ? copyrightText.replace('{year}', currentYear) : copyrightText}</p>
        </div>
      </div>
    </footer>
  );
};

// ============================================
// LAYOUT WRAPPER
// ============================================
export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-grow pb-16 lg:pb-0">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
